/**
 * Merge Variables Module
 *
 * Provides types, constants, and HTML transform helpers for merge-variable
 * tokens embedded inside block text content (e.g. `{{contact.firstName | "fallback"}}`).
 *
 * ## Storage format (persisted in block config `text` field):
 * ```html
 * <span class="merge-variable-placeholder">{{contact.firstName | "John"}}</span>
 * ```
 *
 * ## Display format (rendered inside the canvas contentEditable):
 * Each pill is wrapped with zero-width spaces (ZWS, `\u200B`) so the caret can
 * be positioned immediately before and after the non-editable span.
 * ```html
 * \u200B<span class="merge-variable-placeholder"
 *       contenteditable="false"
 *       data-variable="contact.firstName"
 *       data-fallback="John"
 *       data-label="Contact FirstName">Contact FirstName</span>\u200B
 * ```
 *
 * `toDisplayHtml` converts storage → display before setting innerHTML.
 * `toStorageHtml` converts display → storage before persisting.
 */

/** Unicode zero-width space — used as a cursor anchor around non-editable pills. */
const ZWS = '\u200B';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MergeVariableOption {
  /** The merge-variable key used in the template syntax, e.g. "contact.firstName" */
  variable: string;
  /** Human-friendly label shown inside the pill, e.g. "Contact FirstName" */
  label: string;
  /** Optional grouping category */
  category: string;
}

// ---------------------------------------------------------------------------
// Available options (extend this list later)
// ---------------------------------------------------------------------------

export const MERGE_VARIABLE_OPTIONS: MergeVariableOption[] = [
  { variable: 'contact.firstName', label: 'Contact FirstName', category: 'Contact' },
  { variable: 'contact.lastName', label: 'Contact LastName', category: 'Contact' },
  { variable: 'contact.email', label: 'Contact Email', category: 'Contact' },
];

/** Quick lookup map: variable key → option */
const OPTION_MAP = new Map<string, MergeVariableOption>(
  MERGE_VARIABLE_OPTIONS.map((o) => [o.variable, o]),
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get the human-friendly label for a variable key, with fallback. */
export function getLabelForVariable(variable: string): string {
  return OPTION_MAP.get(variable)?.label ?? variable;
}

/**
 * Build the raw merge-tag string that will be stored in the backend.
 *
 * Examples:
 *  - `toMergeTag('contact.firstName')` → `{{contact.firstName}}`
 *  - `toMergeTag('contact.firstName', 'John')` → `{{contact.firstName | "John"}}`
 */
export function toMergeTag(variable: string, fallback?: string): string {
  if (fallback && fallback.trim().length > 0) {
    return `{{${variable} | "${fallback.trim()}"}}`;
  }
  return `{{${variable}}}`;
}

/**
 * Parse a raw merge-tag string back into its parts.
 *
 * Handles:
 *  - `{{contact.firstName}}`
 *  - `{{contact.firstName | "John"}}`
 */
export function parseMergeTag(raw: string): { variable: string; fallback?: string } | null {
  const match = raw.match(/^\{\{\s*([^\s|]+)\s*(?:\|\s*"([^"]*)")?\s*\}\}$/);
  if (!match) return null;
  return {
    variable: match[1],
    fallback: match[2] ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// HTML transforms
// ---------------------------------------------------------------------------

/**
 * Convert **storage** HTML → **display** HTML.
 *
 * Finds all `<span class="merge-variable-placeholder">{{…}}</span>` in the
 * stored string and replaces them with styled, non-editable pill spans that
 * carry `data-*` attributes for easy round-tripping.
 */
export function toDisplayHtml(storedHtml: string): string {
  // Match the storage-format spans
  return storedHtml.replace(
    /<span\s+class="merge-variable-placeholder">(.*?)<\/span>/gi,
    (_match, inner: string) => {
      const parsed = parseMergeTag(inner.trim());
      if (!parsed) return _match; // leave unparseable spans alone
      const label = getLabelForVariable(parsed.variable);
      const fbAttr = parsed.fallback != null ? ` data-fallback="${escapeAttr(parsed.fallback)}"` : '';
      return (
        `${ZWS}<span class="merge-variable-placeholder"` +
        ` contenteditable="false"` +
        ` data-variable="${escapeAttr(parsed.variable)}"` +
        `${fbAttr}` +
        ` data-label="${escapeAttr(label)}"` +
        `>${escapeHtml(label)}</span>${ZWS}`
      );
    },
  );
}

/**
 * Convert **display** HTML → **storage** HTML.
 *
 * Finds all pill spans (identified by `data-variable`) and replaces them with
 * the compact storage format: `<span class="merge-variable-placeholder">{{…}}</span>`.
 */
export function toStorageHtml(displayHtml: string): string {
  // Match display-format spans with data-variable attribute
  let result = displayHtml.replace(
    /<span\s+[^>]*data-variable="([^"]*?)"[^>]*>.*?<\/span>/gi,
    (_match, variable: string) => {
      // Also extract fallback if present
      const fallbackMatch = _match.match(/data-fallback="([^"]*)"/);
      const fallback = fallbackMatch ? fallbackMatch[1] : undefined;
      const tag = toMergeTag(unescapeAttr(variable), fallback ? unescapeAttr(fallback) : undefined);
      return `<span class="merge-variable-placeholder">${tag}</span>`;
    },
  );
  // Strip zero-width spaces that were added for cursor placement
  result = result.replace(/\u200B/g, '');
  return result;
}

/**
 * Build a display-format pill span string that can be inserted at the cursor.
 */
export function buildPillHtml(variable: string, fallback?: string): string {
  const label = getLabelForVariable(variable);
  const fbAttr = fallback && fallback.trim().length > 0
    ? ` data-fallback="${escapeAttr(fallback.trim())}"`
    : '';
  return (
    `${ZWS}<span class="merge-variable-placeholder"` +
    ` contenteditable="false"` +
    ` data-variable="${escapeAttr(variable)}"` +
    `${fbAttr}` +
    ` data-label="${escapeAttr(label)}"` +
    `>${escapeHtml(label)}</span>${ZWS}`
  );
}

// ---------------------------------------------------------------------------
// Internal escape utilities
// ---------------------------------------------------------------------------

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function unescapeAttr(s: string): string {
  return s.replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ---------------------------------------------------------------------------
// DOM helpers (used in TextBlock for keyboard / clipboard handling)
// ---------------------------------------------------------------------------

/**
 * Walk up from `node` to find a merge-variable pill element,
 * or return `null` if the node is not inside a pill.
 */
export function findPillAncestor(node: Node | null): HTMLSpanElement | null {
  while (node && node !== document.body) {
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      (node as HTMLElement).classList.contains('merge-variable-placeholder')
    ) {
      return node as HTMLSpanElement;
    }
    node = node.parentNode;
  }
  return null;
}

/**
 * Given an HTML string that was taken from the clipboard, re-build any
 * merge-variable pill spans so their attributes are intact.
 *
 * Browsers sometimes alter `contenteditable="false"` and `data-*` attributes
 * when copying. This function normalises them back.  If the HTML contains
 * storage-format spans (`{{…}}` inner text) they are converted to display
 * format.  If it already has `data-variable` spans they are left as-is but
 * re-wrapped with ZWS.
 */
export function sanitizeMergeVarsInHtml(html: string): string {
  // First pass: convert any storage-format spans that survived copy
  let result = toDisplayHtml(html);

  // Second pass: ensure existing display pills have ZWS wrappers.
  // If they already do (from toDisplayHtml) this is a no-op.
  result = result.replace(
    /(?<!\u200B)(<span\s+[^>]*class="merge-variable-placeholder"[^>]*>.*?<\/span>)(?!\u200B)/gi,
    `${ZWS}$1${ZWS}`,
  );

  return result;
}
